import React, { useState } from "react";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EventInsightConfigFields from "./EventInsightConfigFields";

const EventInsightSection = ({ 
  organizationalUnits, 
  masterAccountDetails, 
  selectedNodes, 
  onAccountsAdded,
  onBack 
}) => {
  const dispatch = useDispatch();
  const [isEventInsightYamlLaunched, setIsEventInsightYamlLaunched] = useState(false);
  const [showEventInsightConfig, setShowEventInsightConfig] = useState(false);

  const {
    isGeneratingEventInsightYaml,
    generateEventInsightYamlResponse,
    generateEventInsightYamlError,
    organizationalUnits: organizationalUnitsFromStore,
  } = useSelector((state) => state.organizationalUnits);

  // Get selected member accounts - use the same logic as the main tree
  const getSelectedMemberAccounts = () => {
    const selectedAccounts = organizationalUnits
      .flatMap((ou) => ou.accounts)
      .filter((acc) => {
        const isSelected = selectedNodes.includes(acc.accountId);
        const isNotMaster = acc.accountId !== masterAccountDetails.accountNumber;
        
        return isSelected && isNotMaster;
      });
    
    console.log("🚀 ~ getSelectedMemberAccounts ~ selectedAccounts:", selectedAccounts);
    return selectedAccounts;
  };

  // Check if at least one member account is selected
  const hasSelectedMemberAccounts = () => {
    const selectedAccounts = getSelectedMemberAccounts();
    return selectedAccounts.length > 0;
  };

  // Get member account numbers as comma-separated string
  const getMemberAccountNumbersString = () => {
    const selectedAccounts = getSelectedMemberAccounts();
    const accountNumbers = selectedAccounts.map((acc) => acc.accountId).join(',');
    
    console.log("🚀 ~ getMemberAccountNumbersString ~ selectedNodes:", selectedNodes);
    console.log("🚀 ~ getMemberAccountNumbersString ~ selectedAccounts:", selectedAccounts);
    console.log("🚀 ~ getMemberAccountNumbersString ~ accountNumbers:", accountNumbers);
    
    return accountNumbers;
  };

  const handleGenerateEventInsightYaml = () => {
    const selectedAccounts = getSelectedMemberAccounts();
    const selectedAccountNumbers = selectedAccounts.map((acc) => acc.accountId);

    const payload = {
      memberAccountNumbers: selectedAccountNumbers,
      masterAccountNumber: masterAccountDetails.accountNumber,
    };

    console.log("🚀 ~ Cloud Event YAML Payload:", payload);
    console.log("🚀 ~ Selected Accounts:", selectedAccountNumbers);

    dispatch({
      type: "organizationalUnits/generateEventInsightYaml",
      payload,
    });
  };

  const handleLaunchEventInsightYaml = () => {
    if (generateEventInsightYamlResponse?.yamlPresignedUrl) {
      const encodedURL = encodeURIComponent(
        generateEventInsightYamlResponse.yamlPresignedUrl
      );
      const cfURL = `https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${encodedURL}`;
      window.open(cfURL, "_blank");
      setIsEventInsightYamlLaunched(true);
      // Show config section after launch
      setShowEventInsightConfig(true);
    }
  };

  // Helper function to render Cloud Event buttons
  const renderCloudEventButtons = () => {
    const selectedAccounts = getSelectedMemberAccounts();
    const hasSelectedAccounts = hasSelectedMemberAccounts();

    console.log("🚀 ~ renderCloudEventButtons ~ State:", {
      isGeneratingEventInsightYaml,
      generateEventInsightYamlResponse,
      generateEventInsightYamlError,
      hasYamlPresignedUrl: generateEventInsightYamlResponse?.yamlPresignedUrl,
      hasSelectedAccounts,
      selectedAccounts
    });

    if (isGeneratingEventInsightYaml) {
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} style={{ color: "#F98125" }} />
          <Typography variant="body1" color="textSecondary">
            Generating YAML...
          </Typography>
        </Box>
      );
    }

    if (generateEventInsightYamlError) {
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body2" color="error" mb={1}>
            Error: {generateEventInsightYamlError}
          </Typography>
          <Button
            variant="contained"
            onClick={handleGenerateEventInsightYaml}
            disabled={!hasSelectedAccounts}
            style={{
              backgroundColor: !hasSelectedAccounts ? "#ccc" : "#F98125",
              color: "#fff",
              minWidth: "150px",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 500,
              padding: "8px 16px",
            }}
            sx={{
              "&:hover": {
                backgroundColor: !hasSelectedAccounts ? "#ccc" : "#f37200",
              },
            }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    if (generateEventInsightYamlResponse?.yamlPresignedUrl) {
      return (
        <Button
          variant="contained"
          onClick={handleLaunchEventInsightYaml}
          style={{
            backgroundColor: "#F98125",
            color: "#fff",
            minWidth: "150px",
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 16px",
          }}
          sx={{
            "&:hover": {
              backgroundColor: "#f37200",
            },
          }}
        >
          {isEventInsightYamlLaunched ? "Launched" : "Launch YAML"}
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        onClick={handleGenerateEventInsightYaml}
        disabled={!hasSelectedAccounts}
        style={{
          backgroundColor: !hasSelectedAccounts ? "#ccc" : "#F98125",
          color: "#fff",
          minWidth: "150px",
          borderRadius: "6px",
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 16px",
        }}
        sx={{
          "&:hover": {
            backgroundColor: !hasSelectedAccounts ? "#ccc" : "#f37200",
          },
        }}
      >
        Generate YAML
      </Button>
    );
  };

  return (
    <Box sx={{ width: '100%', padding: '24px 0' }}>
      <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
        Enable Cloud Event
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={6} sx={{ lineHeight: 1.6 }}>
        You can only select/deselect the member accounts that were previously selected. At least one member account must be selected to generate YAML.
      </Typography>

      <Box mb={4}></Box>

      {/* Button layout */}
      <Box
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        sx={{ width: '100%', mb: 3 }}
      >
        <Box display="flex" gap={3}>
          <Button
            variant="outlined"
            onClick={() => {
              if (typeof onAccountsAdded === 'function') onAccountsAdded();
            }}
            style={{
              color: "#F98125",
              borderColor: "#F98125",
              minWidth: "100px",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 500,
              padding: "8px 16px",
            }}
            sx={{
              "&:hover": {
                backgroundColor: "#F98125",
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>
          {renderCloudEventButtons()}
        </Box>
      </Box>

      {/* Cloud Event Configuration Fields - shown after launch */}
      {showEventInsightConfig && (
        <Box mt={4} sx={{ width: '100%' }}>
          <EventInsightConfigFields
            masterAccountNumber={masterAccountDetails.accountNumber}
            masterAccountDetails={masterAccountDetails}
            getMemberAccountNumbers={getMemberAccountNumbersString}
            onSuccess={() => {
              if (typeof onAccountsAdded === 'function') onAccountsAdded();
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default EventInsightSection; 